//frontend/src/components/admin/PatientDietPlan.jsx

import React, { useState, useEffect } from 'react'
import api from '../../services/api'
import { Plus, Download, Trash2, Edit2, X, Check, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

export default function PatientDietPlan({ patientId }) {
    const[plans, setPlans] = useState([])
    const [currentPlan, setCurrentPlan] = useState(null)
    const[templates, setTemplates] = useState([])
    const [showTemplateModal, setShowTemplateModal] = useState(false)
    const [newPlanName, setNewPlanName] = useState('')
    const[selectedTemplate, setSelectedTemplate] = useState(null)
    
    // Options pour le PDF
    const[showWeightsInPdf, setShowWeightsInPdf] = useState(true)
    const [showQuantitiesInPdf, setShowQuantitiesInPdf] = useState(true)
    const [showContactInPdf, setShowContactInPdf] = useState(true)


    // --- AXES DYNAMIQUES ---
    const [dynamicAxes, setDynamicAxes] = useState([])

    // Édition inline
    const [editingItemId, setEditingItemId] = useState(null)
    const[editItemData, setEditItemData] = useState({})

    // Recherche Ciqual
    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const[isSearching, setIsSearching] = useState(false)
    
    const [newItem, setNewItem] = useState({
        meal_time: 'breakfast', food_name: '', quantity: '', notes: '', weight_g: '', ciqual_code: null, ciqual_data: null
    })

    const mealTimes = {
        breakfast: { label: '🌅 Petit-déjeuner', color: 'bg-yellow-50 border-yellow-200' },
        lunch: { label: '☀️ Déjeuner', color: 'bg-orange-50 border-orange-200' },
        snack: { label: '🍎 Goûter', color: 'bg-green-50 border-green-200' },
        dinner: { label: '🌙 Dîner', color: 'bg-blue-50 border-blue-200' }
    }

    useEffect(() => {
        loadPlans()
        loadTemplatesAndSettings()
    }, [patientId])

    const loadPlans = async () => {
        try {
            const response = await api.get(`/api/diet-plans/patients/${patientId}/plans`)
            setPlans(response.data)
            if (response.data.length > 0 && !currentPlan) {
                // On cherche d'abord s'il y a un plan actif publié
                const activePlan = response.data.find(p => p.is_active);
                // Sinon, on prend le plus récent par défaut
                setCurrentPlan(activePlan || response.data[0]);
            }
        } catch (error) { console.error('Erreur chargement plans:', error) }
    }


    const loadTemplatesAndSettings = async () => {
        try {
            const[tplRes, settingsRes] = await Promise.all([
                api.get('/api/diet-plans/templates'),
                api.get('/api/settings/')
            ])
            setTemplates(tplRes.data)
            
            // Chargement des axes dynamiques
            const axesSetting = settingsRes.data.find(s => s.setting_key === 'diet_predefined_axes')
            if (axesSetting && axesSetting.setting_value) {
                setDynamicAxes(JSON.parse(axesSetting.setting_value))
            } else {
                setDynamicAxes(["Bien mastiquer (20 min par repas)", "S'hydrater (1.5L d'eau/jour)"]) // Secours
            }
        } catch (error) { console.error('Erreur chargement', error) }
    }

    // --- GESTION DES AXES DU PLAN ---
    const toggleAxe = async (axe) => {
        if (!currentPlan) return;
        
        let currentAxesList =[];
        if (currentPlan.axes) {
            try {
                currentAxesList = JSON.parse(currentPlan.axes);
                if (!Array.isArray(currentAxesList)) currentAxesList =[];
            } catch (e) {
                // Nettoyage au cas où on lit un vieux format à virgules
                currentAxesList = currentPlan.axes.split(',').map(a => a.trim()).filter(Boolean);
            }
        }

        let newAxes;
        if (currentAxesList.includes(axe)) {
            newAxes = currentAxesList.filter(a => a !== axe);
        } else {
            newAxes =[...currentAxesList, axe];
        }
        
        const axesString = JSON.stringify(newAxes); // <-- ON SAUVEGARDE EN JSON !

        
        try {
            await api.patch(`/api/diet-plans/plans/${currentPlan.id}`, { axes: axesString });
            const updatedPlan = { ...currentPlan, axes: axesString };
            setCurrentPlan(updatedPlan);
            setPlans(plans.map(p => p.id === currentPlan.id ? updatedPlan : p));
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde de l'axe");
        }
    }

    const handleCreatePlan = async (templateId = null) => {
        try {
            const response = await api.post('/api/diet-plans/plans', {
                patient_id: patientId,
                name: newPlanName || `Plan du ${new Date().toLocaleDateString()}`,
                template_id: templateId
            })
            setPlans([response.data, ...plans])
            setCurrentPlan(response.data)
            setShowTemplateModal(false)
            setNewPlanName('')
            setSelectedTemplate(null)
            toast.success("Plan créé !")
        } catch (error) { toast.error('Erreur création plan') }
    }

    const handleDeletePlan = async () => {
        if (!currentPlan) return
        if (!confirm('Voulez-vous vraiment supprimer ce plan alimentaire ? Cette action est irréversible.')) return
        try {
            await api.delete(`/api/diet-plans/plans/${currentPlan.id}`)
            toast.success("Plan supprimé !")
            const updatedPlans = plans.filter(p => p.id !== currentPlan.id)
            setPlans(updatedPlans)
            setCurrentPlan(updatedPlans.length > 0 ? updatedPlans[0] : null)
        } catch (error) { toast.error('Erreur lors de la suppression') }
    }

    const handleSetActivePlan = async () => {
        if (!currentPlan) return
        try {
            await api.patch(`/api/diet-plans/plans/${currentPlan.id}/set-active`)
            toast.success("Plan activé sur le portail patient !")
            
            // Mise à jour de l'état local (is_active)
            const updatedPlans = plans.map(p => ({
                ...p, 
                is_active: p.id === currentPlan.id
            }))
            setPlans(updatedPlans)
            setCurrentPlan({ ...currentPlan, is_active: true })
        } catch (error) {
            toast.error("Erreur lors de l'activation")
        }
    }

    const handleDeactivatePlan = async () => {
        if (!currentPlan) return
        try {
            await api.patch(`/api/diet-plans/plans/${currentPlan.id}/deactivate`)
            toast.success("Plan retiré du portail !")
            
            const updatedPlans = plans.map(p => ({
                ...p, 
                is_active: p.id === currentPlan.id ? false : p.is_active
            }))
            setPlans(updatedPlans)
            setCurrentPlan({ ...currentPlan, is_active: false })
        } catch (error) {
            toast.error("Erreur lors de la désactivation")
        }
    }

    const handleAddItem = async (mealKey) => {
        if (newItem.meal_time !== mealKey || !newItem.food_name.trim()) return
        try {
            const currentItems = currentPlan.items ||[]
            const payload = {
                ...newItem,
                weight_g: newItem.weight_g ? parseInt(newItem.weight_g) : null,
                sort_order: currentItems.length
            }
            const response = await api.post(`/api/diet-plans/plans/${currentPlan.id}/items`, payload)
            
            // On met à jour le plan actif ET le tableau de tous les plans
            const updatedPlan = { ...currentPlan, items: [...currentItems, response.data] }
            setCurrentPlan(updatedPlan)
            setPlans(plans.map(p => p.id === currentPlan.id ? updatedPlan : p))
            
            setNewItem({ meal_time: mealKey, food_name: '', quantity: '', notes: '', weight_g: '', ciqual_code: null, ciqual_data: null })
        } catch (error) { toast.error('Erreur ajout') }
    }


    const startEditItem = (item) => {
        setEditingItemId(item.id)
        setEditItemData({ ...item })
    }

    const handleUpdateItem = async () => {
        try {
            const res = await api.patch(`/api/diet-plans/items/${editingItemId}`, {
                food_name: editItemData.food_name,
                quantity: editItemData.quantity,
                notes: editItemData.notes,
                weight_g: editItemData.weight_g ? parseInt(editItemData.weight_g) : null,
                ciqual_code: editItemData.ciqual_code,
            })
            
            const updatedItems = currentPlan.items.map(i => i.id === editingItemId ? res.data : i)
            setCurrentPlan({ ...currentPlan, items: updatedItems })
            setEditingItemId(null)
            toast.success("Aliment modifié !")
        } catch (e) { toast.error("Erreur modification") }
    }

    const handleDeleteItem = async (itemId) => {
        if (!confirm('Supprimer cet aliment ?')) return
        try {
            await api.delete(`/api/diet-plans/items/${itemId}`)
            setCurrentPlan({ ...currentPlan, items: currentPlan.items.filter(item => item.id !== itemId) })
        } catch (error) { console.error('Erreur suppression:', error) }
    }

    const handlePreviewPDF = async () => {
        try {
            toast.loading("Génération de l'aperçu...", { id: 'pdf_preview' })
            const newWindow = window.open('', '_blank')
            const response = await api.get(`/api/diet-plans/plans/${currentPlan.id}/pdf`, {
                params: { show_weights: showWeightsInPdf, show_quantities: showQuantitiesInPdf, show_contact: showContactInPdf },
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
            newWindow.location.href = url
            toast.success("Aperçu généré !", { id: 'pdf_preview' })
        } catch (error) { toast.error('Erreur PDF', { id: 'pdf_preview' }) }
    }

    const handleDownloadPDF = async () => {
        try {
            toast.loading("Téléchargement...", { id: 'pdf_dl' })
            const response = await api.get(`/api/diet-plans/plans/${currentPlan.id}/pdf`, {
                params: { show_weights: showWeightsInPdf, show_quantities: showQuantitiesInPdf, show_contact: showContactInPdf },
                responseType: 'blob'
            })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `plan_${currentPlan.name}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            toast.success("Téléchargé !", { id: 'pdf_dl' })
        } catch (error) { toast.error('Erreur PDF', { id: 'pdf_dl' }) }
    }


    const getItemMacros = (item) => {
        if (!item.weight_g || !item.ciqual_data) return null;
        const ratio = item.weight_g / 100;
        return { kcal: item.ciqual_data.energie_kcal * ratio, prot: item.ciqual_data.proteines * ratio, gluc: item.ciqual_data.glucides * ratio, lip: item.ciqual_data.lipides * ratio };
    }

    const getMealTotals = (mealKey) => {
        if (!currentPlan?.items) return { kcal: 0, prot: 0, gluc: 0, lip: 0 };
        const items = currentPlan.items.filter(i => i.meal_time === mealKey);
        const totals = { kcal: 0, prot: 0, gluc: 0, lip: 0 };
        items.forEach(item => {
            const macros = getItemMacros(item);
            if (macros) { totals.kcal += macros.kcal; totals.prot += macros.prot; totals.gluc += macros.gluc; totals.lip += macros.lip; }
        });
        return totals;
    }

    const getDailyTotal = () => {
        const total = { kcal: 0, prot: 0, gluc: 0, lip: 0 };
        ['breakfast', 'lunch', 'snack', 'dinner'].forEach(meal => {
            const mTotal = getMealTotals(meal);
            total.kcal += mTotal.kcal; total.prot += mTotal.prot; total.gluc += mTotal.gluc; total.lip += mTotal.lip;
        });
        return total;
    }

    const groupedItems = () => {
        if (!currentPlan?.items) return {}
        return currentPlan.items.reduce((acc, item) => {
            if (!acc[item.meal_time]) acc[item.meal_time] =[]
            acc[item.meal_time].push(item)
            return acc
        }, {})
    }
    const grouped = groupedItems()

    const handleSearchCiqual = async (text) => {
        if (editingItemId) {
            setEditItemData(prev => ({ ...prev, food_name: text, ciqual_code: null, ciqual_data: null }))
        } else {
            setNewItem(prev => ({ ...prev, food_name: text, ciqual_code: null, ciqual_data: null }))
        }
        
        if (text.length < 2) { setSuggestions([]); setShowSuggestions(false); return }

        setIsSearching(true)
        try {
            const res = await api.get(`/api/ciqual/search?q=${encodeURIComponent(text)}`)
            setSuggestions(res.data)
            setShowSuggestions(true)
        } catch (error) { console.error("Erreur Ciqual", error) } 
        finally { setIsSearching(false) }
    }

    const selectCiqualItem = (item) => {
        if (editingItemId) {
            setEditItemData(prev => ({ ...prev, food_name: item.nom_fr, ciqual_code: item.code, ciqual_data: item }))
        } else {
            setNewItem(prev => ({ ...prev, food_name: item.nom_fr, ciqual_code: item.code, ciqual_data: item }))
        }
        setShowSuggestions(false)
    }

    

    return (
        <div className="space-y-6 relative">
            {(!currentPlan && plans.length === 0) ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                    <div className="text-gray-300 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </div>
                    <p className="text-gray-500 mb-4">Aucun plan alimentaire pour ce patient</p>
                    <button onClick={() => setShowTemplateModal(true)} className="btn btn-primary">Créer un premier plan</button>
                </div>
            ) : (
                <>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <div className="relative">
                                <select 
                                    value={currentPlan?.id || ''}
                                    onChange={(e) => setCurrentPlan(plans.find(p => p.id === parseInt(e.target.value)))}
                                    className="input font-bold text-gray-700 w-full sm:w-64 pr-8"
                                >
                                    {/* On trie à la volée : le plan actif (true) passe en premier */}
                                    {[...plans].sort((a, b) => b.is_active - a.is_active).map(plan => (
                                        <option key={plan.id} value={plan.id}>
                                            {plan.is_active ? '🟢 (Actif) ' : '⚫ '}{plan.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            {!currentPlan.is_active ? (
                                <button 
                                    onClick={handleSetActivePlan} 
                                    className="px-3 py-2 bg-blue-50 text-blue-600 text-xs font-bold rounded-lg border border-blue-200 hover:bg-blue-100 transition-colors whitespace-nowrap"
                                    title="Rendre visible sur l'application du patient"
                                >
                                    Publier sur le portail
                                </button>
                            ) : (
                                <button 
                                    onClick={handleDeactivatePlan} 
                                    className="px-3 py-2 bg-gray-50 text-gray-500 text-xs font-bold rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors whitespace-nowrap"
                                    title="Cacher ce plan du portail patient"
                                >
                                    Retirer du portail
                                </button>
                            )}

                            <button onClick={() => setShowTemplateModal(true)} className="p-2 bg-sage-100 text-sage-700 rounded-lg hover:bg-sage-200 transition-colors shrink-0" title="Nouveau plan"><Plus className="w-5 h-5" /></button>
                            <button onClick={handleDeletePlan} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors shrink-0" title="Supprimer ce plan"><Trash2 className="w-5 h-5" /></button>
                        </div>
                        
                        <div className="flex items-center gap-4 w-full sm:w-auto">
                            <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    checked={showQuantitiesInPdf} 
                                    onChange={(e) => setShowQuantitiesInPdf(e.target.checked)}
                                    className="rounded text-sage-600 focus:ring-sage-500 w-4 h-4"
                                />
                                Quantités
                            </label>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase cursor-pointer border-l border-gray-200 pl-4">
                                <input 
                                    type="checkbox" 
                                    checked={showWeightsInPdf} 
                                    onChange={(e) => setShowWeightsInPdf(e.target.checked)}
                                    className="rounded text-sage-600 focus:ring-sage-500 w-4 h-4"
                                />
                                Grammages
                            </label>
                            <label className="flex items-center gap-1.5 text-xs font-bold text-gray-500 uppercase cursor-pointer border-l border-gray-200 pl-4">
                                <input 
                                    type="checkbox" 
                                    checked={showContactInPdf} 
                                    onChange={(e) => setShowContactInPdf(e.target.checked)}
                                    className="rounded text-sage-600 focus:ring-sage-500 w-4 h-4"
                                />
                                En-tête
                            </label>
                            <div className="flex gap-2 ml-2">
                                <button onClick={handlePreviewPDF} disabled={!currentPlan} className="btn bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center justify-center gap-2 text-sm" title="Aperçu">
                                    <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={handleDownloadPDF} disabled={!currentPlan} className="btn btn-secondary flex items-center justify-center gap-2 text-sm" title="Télécharger">
                                    <Download className="w-4 h-4" /> PDF
                                </button>
                            </div>
                        </div>
                    </div>

                    {currentPlan && (
                        <div className="px-6 py-3 bg-sage-50 border border-sage-100 rounded-xl flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 text-sm shadow-sm">
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-sage-800">Total Journée :</span>
                                <span className="font-bold text-lg">{Math.round(getDailyTotal().kcal)} kcal</span>
                            </div>
                            <div className="flex gap-3 text-gray-600 text-xs items-center font-medium">
                                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">P: {Math.round(getDailyTotal().prot)}g</span>
                                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded">G: {Math.round(getDailyTotal().gluc)}g</span>
                                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">L: {Math.round(getDailyTotal().lip)}g</span>
                            </div>
                        </div>
                    )}

                    {/* AXES DE TRAVAIL DYNAMIQUES */}
                    {currentPlan && dynamicAxes.length > 0 && (
                        <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm animate-in fade-in">
                            <h3 className="font-bold text-gray-700 text-xs uppercase mb-3 flex items-center gap-2">
                                🎯 Axes de travail (Imprimés sur le PDF)
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {dynamicAxes.map(axe => {
                                    // Lecture robuste pour l'affichage (JSON ou ancienne version à virgules)
                                    let currentAxesList =[];
                                    if (currentPlan.axes) {
                                        try {
                                            currentAxesList = JSON.parse(currentPlan.axes);
                                            if (!Array.isArray(currentAxesList)) currentAxesList = [];
                                        }   catch (e) {
                                                currentAxesList = currentPlan.axes.split(',').map(a => a.trim());
                                            }
                                        }
                                        const isSelected = currentAxesList.includes(axe);
                                        return (
                                        <button
                                            key={axe}
                                            onClick={() => toggleAxe(axe)}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                                isSelected 
                                                ? 'bg-sage-600 text-white border-sage-600 shadow-sm' 
                                                : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-sage-50 hover:text-sage-700'
                                            }`}
                                        >
                                            {axe}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        {Object.entries(mealTimes).map(([key, config]) => {
                            const mealTotal = getMealTotals(key);
                            return (
                            <div key={key} className={`rounded-xl border-2 ${config.color} p-4 bg-opacity-30 bg-white flex flex-col h-full`}>
                                <h3 className="font-bold text-gray-700 mb-3 pb-2 border-b border-gray-200/50 flex justify-between items-center">
                                    <div className="flex items-center gap-2">
                                        {config.label}
                                        <span className="text-xs bg-white px-2 py-0.5 rounded-full border border-gray-200 text-gray-400">{(grouped[key] ||[]).length}</span>
                                    </div>
                                    <span className="text-xs font-bold text-gray-400">{Math.round(mealTotal.kcal)} kcal</span>
                                </h3>
                                
                                <div className="space-y-2 mb-4 flex-1">
                                    {(grouped[key] ||[]).map(item => {
                                        const macros = getItemMacros(item);
                                        return (
                                        <div key={item.id} className="bg-white rounded-lg p-2.5 text-sm border border-gray-100 shadow-sm hover:shadow-md transition-all group relative">
                                            
                                            {editingItemId === item.id ? (
                                                <div className="flex flex-col gap-2 animate-in fade-in">
                                                    <div className="relative">
                                                        <input 
                                                            className="w-full px-2 py-1.5 text-xs border-sage-300 rounded focus:ring-2 focus:ring-sage-200 focus:border-sage-500"
                                                            value={editItemData.food_name}
                                                            onChange={(e) => handleSearchCiqual(e.target.value)}
                                                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                                            autoFocus
                                                        />
                                                        {showSuggestions && suggestions.length > 0 && (
                                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
                                                                {suggestions.map((s) => (
                                                                    <div key={s.code} onMouseDown={() => selectCiqualItem(s)} className="px-3 py-2 hover:bg-sage-50 cursor-pointer text-xs border-b border-gray-50 flex justify-between">
                                                                        <span className="font-bold text-gray-700">{s.nom_fr}</span>
                                                                        <span className="text-gray-400">{Math.round(s.energie_kcal)} kcal</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <div className="relative w-20">
                                                            <input 
                                                                type="number"
                                                                className="w-full px-2 py-1.5 text-xs border-sage-300 rounded text-right pr-5"
                                                                placeholder="g"
                                                                value={editItemData.weight_g || ''}
                                                                onChange={e => setEditItemData({...editItemData, weight_g: e.target.value})}
                                                            />
                                                            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">g</span>
                                                        </div>
                                                        <input 
                                                            className="flex-1 px-2 py-1.5 text-xs border-sage-300 rounded"
                                                            placeholder="Qté (ex: 2 pcs)"
                                                            value={editItemData.quantity || ''}
                                                            onChange={e => setEditItemData({...editItemData, quantity: e.target.value})}
                                                        />
                                                    </div>
                                                    <input 
                                                        className="w-full px-2 py-1.5 text-xs border-sage-300 rounded text-gray-500"
                                                        placeholder="Note (facultative)"
                                                        value={editItemData.notes || ''}
                                                        onChange={e => setEditItemData({...editItemData, notes: e.target.value})}
                                                    />
                                                    <div className="flex gap-1 justify-end mt-1">
                                                        <button onClick={() => setEditingItemId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
                                                        <button onClick={handleUpdateItem} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-1 right-1 bg-white/90 rounded p-0.5">
                                                        <button onClick={() => startEditItem(item)} className="p-1 text-gray-400 hover:text-blue-500 rounded" title="Modifier"><Edit2 className="w-3 h-3"/></button>
                                                        <button onClick={() => handleDeleteItem(item.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Supprimer"><Trash2 className="w-3 h-3" /></button>
                                                    </div>
                                                    
                                                    <div className="flex justify-between items-start pr-12">
                                                        <span className="font-semibold text-gray-800 leading-tight">
                                                            {item.food_name}
                                                            {(item.quantity || item.weight_g) && (
                                                                <span className="text-gray-500 text-xs ml-1 font-normal block sm:inline">
                                                                    ({item.quantity ? item.quantity : ''}
                                                                     {item.quantity && item.weight_g ? ' / ' : ''}
                                                                     {item.weight_g ? `${item.weight_g}g` : ''})
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {macros && (
                                                        <div className="mt-1">
                                                            <span className="text-[10px] text-sage-600 bg-sage-50 px-1.5 py-0.5 rounded font-medium whitespace-nowrap">
                                                                {Math.round(macros.kcal)} kcal
                                                            </span>
                                                        </div>
                                                    )}
                                                    {item.notes && <div className="mt-1.5 text-xs text-gray-400 italic">- {item.notes}</div>}
                                                </>
                                            )}
                                        </div>
                                    )})}
                                </div>

                                <div className="bg-white/50 p-2 rounded-lg border border-dashed border-gray-300">
                                    <div className="relative">
                                        <input 
                                            type="text" placeholder="+ Ajouter aliment..."
                                            value={newItem.meal_time === key ? newItem.food_name : ''}
                                            onChange={(e) => { setNewItem({ ...newItem, meal_time: key }); handleSearchCiqual(e.target.value) }}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key)}
                                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                            className="w-full text-sm bg-transparent border-none p-0 focus:ring-0 placeholder-gray-400"
                                        />
                                        {newItem.meal_time === key && showSuggestions && suggestions.length > 0 && (
                                            <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                                {suggestions.map((s) => (
                                                    <div key={s.code} onMouseDown={() => selectCiqualItem(s)} className="px-3 py-2 hover:bg-sage-50 cursor-pointer text-xs border-b border-gray-50 flex justify-between">
                                                        <div className="font-bold text-gray-700">{s.nom_fr}</div>
                                                        <div className="text-gray-400">{Math.round(s.energie_kcal)} kcal</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {newItem.meal_time === key && newItem.food_name && (
                                        <div className="mt-2 space-y-2 animate-in fade-in slide-in-from-top-1">
                                            <div className="flex gap-2">
                                                <div className="relative w-1/2">
                                                    <input type="number" placeholder="Poids" value={newItem.weight_g || ''} onChange={(e) => setNewItem({...newItem, weight_g: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key)} className="w-full text-xs border border-gray-200 rounded px-2 py-1 pr-5" />
                                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400">g</span>
                                                </div>
                                                <input type="text" placeholder="Qté (ex: 2 tranches)" value={newItem.quantity} onChange={(e) => setNewItem({...newItem, quantity: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key)} className="w-1/2 text-xs border border-gray-200 rounded px-2 py-1" />
                                            </div>
                                            <input type="text" placeholder="Note (facultatif)" value={newItem.notes} onChange={(e) => setNewItem({...newItem, notes: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && handleAddItem(key)} className="w-full text-xs border border-gray-200 rounded px-2 py-1" />
                                            <button onClick={() => handleAddItem(key)} className="w-full btn btn-primary py-1 text-xs">Valider</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )})}
                        {/* MENTION LÉGALE CIQUAL OBLIGATOIRE */}
                        <p className="text-[9px] text-gray-400">
                            Anses. 2025. Table de composition nutritionnelle des aliments Ciqual.
                        </p>
                    </div>
                </>
            )}

            {showTemplateModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">Nouveau Plan Alimentaire</h2>
                            <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600"><Plus className="w-6 h-6 rotate-45" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-600 uppercase mb-2">Nom du plan</label>
                                <input type="text" value={newPlanName} onChange={(e) => setNewPlanName(e.target.value)} placeholder="Ex: Plan Mars 2026 - Perte de poids" className="input w-full" autoFocus />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-600 uppercase mb-3">Importer depuis un modèle</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div onClick={() => setSelectedTemplate(null)} className={`p-4 border-2 rounded-xl cursor-pointer transition-all flex items-center justify-between ${selectedTemplate === null ? 'border-sage-500 bg-sage-50 ring-1 ring-sage-500' : 'border-gray-100 hover:border-gray-300'}`}>
                                        <div><div className="font-bold text-gray-800">Plan Vierge</div><div className="text-xs text-gray-500 mt-1">Partir de zéro</div></div>
                                        {selectedTemplate === null && <div className="w-3 h-3 bg-sage-500 rounded-full" />}
                                    </div>
                                    {templates.map(template => (
                                        <div key={template.id} onClick={() => setSelectedTemplate(template)} className={`p-4 border-2 rounded-xl cursor-pointer transition-all flex items-center justify-between ${selectedTemplate?.id === template.id ? 'border-sage-500 bg-sage-50 ring-1 ring-sage-500' : 'border-gray-100 hover:border-gray-300'}`}>
                                            <div><div className="font-bold text-gray-800 truncate pr-2">{template.name}</div><div className="text-xs text-gray-500 mt-1">{template.items.length} aliments pré-remplis</div></div>
                                            {selectedTemplate?.id === template.id && <div className="w-3 h-3 bg-sage-500 rounded-full flex-shrink-0" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                            <button onClick={() => setShowTemplateModal(false)} className="btn bg-white border border-gray-300 text-gray-600">Annuler</button>
                            <button onClick={() => handleCreatePlan(selectedTemplate?.id)} className="btn btn-primary px-8">Créer le plan</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}